import { Container, Typography } from '@mui/material';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import React, { useEffect, useState } from 'react';

import Documentation from '../../api/documentation';
import { markdownOptions } from '../../helpers/markdownOptions';
import { parseWikiFrontmatter } from '../../helpers/wikiContent';
import useApiQuery from '../../hooks/useApiQuery';
import SomethingWentWrong from '../Error/SomethingWentWrong';

import { useUserData } from '@/redux/selectors';

interface WhatsNewContent {
  title: string;
  content: string;
}

const WhatsNew = (): React.JSX.Element | null => {
  const [content, setContent] = useState<WhatsNewContent | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const { makeApiCall: getWhatsNewCall } = useApiQuery<WhatsNewContent>(
    Documentation.getWhatsNew.bind(Documentation) as (
      ...args: unknown[]
    ) => Promise<WhatsNewContent | { data?: WhatsNewContent }>
  );
  const { locale } = useUserData();

  useEffect(() => {
    let isCurrent = true;

    // Clear the previous locale's content immediately so it isn't shown
    // indefinitely if this locale's request fails.
    setContent(null);
    setHasFailed(false);

    const fetchWhatsNew = async (): Promise<void> => {
      try {
        const data: WhatsNewContent | null = await getWhatsNewCall(locale);
        if (!isCurrent) return;

        if (data) {
          setContent(data);
        } else {
          setHasFailed(true);
        }
      } catch {
        // `makeApiCall` already notified and rethrows; show the error state.
        if (isCurrent) setHasFailed(true);
      }
    };

    void fetchWhatsNew();

    return () => {
      isCurrent = false;
    };
  }, [getWhatsNewCall, locale]);

  if (hasFailed) {
    return <SomethingWentWrong />;
  }

  if (!content) {
    return null;
  }

  // GitLab's wiki API derives `title` from the page slug, not from any
  // frontmatter inside the content - the German page shares its filename
  // with the English one, so this is the only way to get a localized title.
  const parsedContent = parseWikiFrontmatter(content.content);

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Typography
        variant='h3'
        gutterBottom
        sx={{ fontWeight: 'bold', mb: 3 }}
      >
        {parsedContent.title ?? content.title}
      </Typography>
      <Markdown options={markdownOptions as MarkdownToJSX.Options}>
        {parsedContent.content}
      </Markdown>
    </Container>
  );
};

export default WhatsNew;
